import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable as Droppable } from './StrictModeDroppable';
import AuthContext from '../../contexts/AuthContext';
import ThemeContext from '../../contexts/ThemeContext';
import { useSocket } from '../../contexts/SocketContext';
import axios from 'axios';
import CardItem from '../cards/CardItem';
import CardModal from '../cards/CardModal';
import CardContextMenu from '../cards/CardContextMenu';
import ListView from './ListView';
import TableView from './TableView';
import CalendarView from './CalendarView';
import FilterBar from './FilterBar';
import { getFileUrl } from '../../utils/fileUtil';
import KnowledgeFileEditor from '../common/KnowledgeFileEditor';
import MarketingView from '../marketing/MarketingView';
import { stripMarkdown } from '../../utils/markdownUtil';
import BoardMapsMenu from '../mindmap/BoardMapsMenu';
import BoardFlowsMenu from '../flowmap/BoardFlowsMenu';
import TicklerPanel from './TicklerPanel';

// The GTD "Command Center" board — the Tickler panel button only shows here.
const VIEW_OPTIONS = [
  { id: 'kanban', label: 'Kanban', title: 'Kanban board', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
  { id: 'list', label: 'List', title: 'Simple list', icon: 'M4 6h16M4 12h16M4 18h16' },
  { id: 'table', label: 'Table', title: 'Groups and items, Monday.com style', icon: 'M3 10h18M3 14h18M10 4v16M3 6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6z' },
  { id: 'calendar', label: 'Calendar', title: 'Due dates on a calendar', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' }
];

const GTD_BOARD_ID = '6a54edb300e892c0c21323ba';

const PRIORITY_RANK = { Urgent: 4, High: 3, Medium: 2, Low: 1 };

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Pure, display-only filter applied to the already-loaded cards (no backend round-trip).
const matchesFilters = (card, f) => {
  if (f.q) {
    const q = f.q.toLowerCase();
    const hay = `${card.title || ''} ${card.description || ''}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (f.priorities.length && !f.priorities.includes(card.priority)) return false;

  if (f.assignees.length) {
    const ids = (card.assignedTo || []).map(u => (u._id || u).toString());
    const matchUser = f.assignees.some(a => a !== 'unassigned' && ids.includes(a));
    const matchUnassigned = f.assignees.includes('unassigned') && ids.length === 0;
    if (!matchUser && !matchUnassigned) return false;
  }

  if (f.labels.length) {
    const texts = (card.labels || []).map(l => l.text);
    if (!f.labels.some(t => texts.includes(t))) return false;
  }

  if (f.tags.length) {
    const tags = card.tags || [];
    if (!f.tags.some(t => tags.includes(t))) return false;
  }

  if (f.due) {
    const has = !!card.dueDate;
    if (f.due === 'none') {
      if (has) return false;
    } else {
      if (!has) return false;
      const due = new Date(card.dueDate);
      const today = startOfToday();
      const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
      const weekEnd = new Date(today); weekEnd.setDate(today.getDate() + 7);
      if (f.due === 'overdue' && !(due < today && !card.completedAt)) return false;
      if (f.due === 'today' && !(due >= today && due < tomorrow && !card.completedAt)) return false;
      if (f.due === 'week' && !(due >= today && due < weekEnd && !card.completedAt)) return false;
    }
  }

  return true;
};

const sortCards = (cards, sortKey) => {
  const arr = [...cards];
  switch (sortKey) {
    case 'due-asc':
      return arr.sort((a, b) => {
        const ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return ad - bd;
      });
    case 'priority-desc':
      return arr.sort((a, b) => (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0));
    case 'created-desc':
      return arr.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    case 'title-asc':
      return arr.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    default:
      return arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
};

// API implementation to fetch board and its cards
const fetchBoardData = async (boardId) => {
  const token = localStorage.getItem('token');
  const config = {
    headers: { 'x-auth-token': token }
  };
  
  const [boardRes, cardsRes] = await Promise.all([
    axios.get(`/api/boards/${boardId}`, config),
    axios.get(`/api/cards/board/${boardId}`, config)
  ]);
  
  return {
    ...boardRes.data,
    cards: cardsRes.data
  };
};

const Board = ({ boardId }) => {
  const { isAdmin, token, currentUser } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const socket = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('kanban'); // 'kanban' | 'list' | 'table' | 'calendar'

  // Kanban zoom: scrollwheel (desktop) / pinch (mobile) scales the board via CSS zoom,
  // persisted per board. Range clamped so cards stay usable.
  const [boardZoom, setBoardZoom] = useState(() => {
    const saved = parseFloat(localStorage.getItem(`kanban-zoom-${boardId}`));
    return saved >= 0.4 && saved <= 1.5 ? saved : 1;
  });
  const kanbanScrollRef = useRef(null); // outer scroll container (unzoomed)
  const kanbanZoomRef = useRef(null); // inner zoomed content div
  const boardZoomRef = useRef(1);
  boardZoomRef.current = boardZoom;
  const dragActiveRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(`kanban-zoom-${boardId}`, String(boardZoom));
  }, [boardZoom, boardId]);

  // Native wheel/touch listeners: React's synthetic handlers are passive for these
  // events, and we need preventDefault so the page doesn't scroll/zoom underneath.
  useEffect(() => {
    const el = kanbanScrollRef.current;
    if (!el) return;

    const zoomBy = (factor, cx, cy) => {
      if (dragActiveRef.current) return;
      setBoardZoom(prev => {
        const next = Math.min(1.5, Math.max(0.4, Math.round(prev * factor * 1000) / 1000));
        if (next === prev) return prev;
        // Keep the point under the cursor stationary: rescale scroll offsets after
        // the zoomed content re-renders.
        const rect = el.getBoundingClientRect();
        const ox = cx - rect.left;
        const oy = cy - rect.top;
        const scale = next / prev;
        requestAnimationFrame(() => {
          el.scrollLeft = (el.scrollLeft + ox) * scale - ox;
          el.scrollTop = (el.scrollTop + oy) * scale - oy;
        });
        return next;
      });
    };

    const onWheel = (e) => {
      e.preventDefault();
      zoomBy(Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY);
    };

    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    let pinch = null;
    const onTouchStart = (e) => { if (e.touches.length === 2) pinch = dist(e.touches); };
    const onTouchMove = (e) => {
      if (e.touches.length !== 2 || pinch === null) return;
      e.preventDefault();
      const d = dist(e.touches);
      zoomBy(d / pinch, (e.touches[0].clientX + e.touches[1].clientX) / 2, (e.touches[0].clientY + e.touches[1].clientY) / 2);
      pinch = d;
    };
    const onTouchEnd = (e) => { if (e.touches.length < 2) pinch = null; };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [activeView, loading]);

  // react-beautiful-dnd measures in screen px but applies drag transforms inside the
  // zoomed subtree, so any zoom !== 1 makes drags mistrack. Snap to 100% before rbd
  // captures dimensions (this hook runs synchronously before capture), restore on drop.
  const handleBeforeCapture = () => {
    dragActiveRef.current = true;
    if (kanbanZoomRef.current && boardZoomRef.current !== 1) {
      kanbanZoomRef.current.style.zoom = '1';
    }
  };

  // Board filters/sort (hydrated from the URL so views are shareable/bookmarkable).
  const [filters, setFilters] = useState(() => {
    const csv = (key) => (searchParams.get(key) ? searchParams.get(key).split(',').filter(Boolean) : []);
    return {
      q: searchParams.get('q') || '',
      assignees: csv('assignee'),
      labels: csv('label'),
      tags: csv('tag'),
      priorities: csv('priority'),
      due: searchParams.get('due') || '',
      sort: searchParams.get('sort') || '',
    };
  });

  // Keep the URL in sync with the active filters (preserving any unrelated params).
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const setOrDel = (k, v) => { if (v && v.length) p.set(k, v); else p.delete(k); };
      setOrDel('q', filters.q);
      setOrDel('assignee', filters.assignees.join(','));
      setOrDel('label', filters.labels.join(','));
      setOrDel('tag', filters.tags.join(','));
      setOrDel('priority', filters.priorities.join(','));
      setOrDel('due', filters.due);
      setOrDel('sort', filters.sort);
      return p;
    }, { replace: true });
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter option lists derived from the full (unfiltered) card set so options stay stable.
  const assigneeOptions = useMemo(() => {
    const map = new Map();
    (board?.cards || []).forEach(c => (c.assignedTo || []).forEach(u => {
      if (u && u._id) map.set(u._id.toString(), u);
    }));
    return Array.from(map.values());
  }, [board]);

  const labelOptions = useMemo(() => {
    const map = new Map();
    (board?.cards || []).forEach(c => (c.labels || []).forEach(l => {
      if (l && l.text) map.set(l.text, l);
    }));
    return Array.from(map.values());
  }, [board]);

  const tagOptions = useMemo(() => {
    const set = new Set();
    (board?.cards || []).forEach(c => (c.tags || []).forEach(t => { if (t) set.add(t); }));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [board]);

  const filteredCards = useMemo(
    () => (board?.cards || []).filter(c => matchesFilters(c, filters)),
    [board, filters]
  );
  
  // State for Add Card Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssigneeList, setShowAssigneeList] = useState(false);
  const [newCardData, setNewCardData] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    column: '',
    cardType: 'task',
    assignedTo: [],
    labels: [],
    contactInfo: {
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      jobTitle: ''
    },
    socialMedia: {
      platforms: [],
      postCopy: '',
      platformCopy: {
        facebook: '',
        linkedin: '',
        x: ''
      },
      ctaLink: '',
      scheduledDate: ''
    }
  });

  // State for Card Detail Modal
  const [selectedCardId, setSelectedCardId] = useState(null);
  // Right-click quick-actions menu: { x, y, cardId } | null
  const [contextMenu, setContextMenu] = useState(null);

  // Open a specific card when the URL carries ?cardId= (from the dashboard master list,
  // notifications, etc.), then strip the param so it doesn't reopen later.
  //
  // Keyed on the param itself, not just `loading`: this component stays mounted
  // while you move between boards and `loading` only ever flips false once, so
  // watching it alone meant a notification clicked from inside a board changed
  // the URL and opened nothing. Stripping the param sets it to null, which
  // re-runs this and returns early.
  const cardIdParam = searchParams.get('cardId');
  useEffect(() => {
    if (loading || !cardIdParam) return;
    setSelectedCardId(cardIdParam);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('cardId');
      return p;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cardIdParam]);

  // State for Column Management
  const [editingColumn, setEditingColumn] = useState(null); // columnName
  const [newColumnName, setNewColumnName] = useState('');
  const [isAddingColumn, setIsAddingColumn] = useState(false);

  // State for the Tickler panel (GTD board only)
  const [showTickler, setShowTickler] = useState(false);

  // State for Board Settings
  const [showSettings, setShowSettings] = useState(false);
  const [editingColumnSettings, setEditingColumnSettings] = useState(null); // columnName
  const [archivedCards, setArchivedCards] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  
  // Knowledge files state
  const [editingKnowledgeFile, setEditingKnowledgeFile] = useState(null);

  // Board deletion state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteBoardName, setDeleteConfirmName] = useState('');

  // State for AI & Templates
  const [templates, setTemplates] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiQuestions, setAiQuestions] = useState('');
  const [userAiResponse, setUserAiResponse] = useState('');
  const [showSaveTemplateForm, setShowSaveTemplateForm] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateSuccess, setSaveTemplateSuccess] = useState(false);

  // Bulk selection state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [bulkMoveTarget, setBulkMoveTarget] = useState('');
  const [bulkPriorityTarget, setBulkPriorityTarget] = useState('');
  const [showBulkAssignDropdown, setShowBulkAssignDropdown] = useState(false);
  const [showConsolidateModal, setShowConsolidateModal] = useState(false);
  const [consolidateForm, setConsolidateForm] = useState({ title: '', priority: 'Medium', dueDate: '', column: '', originalsAction: 'archive' });
  const [consolidating, setConsolidating] = useState(false);
  const assignDropdownRef = useRef(null);

  const availableIcons = ['📋', '🚀', '🔥', '✅', '⚠️', '🎯', '💡', '🛠️', '🎨', '🔍'];
  const themeColors = ['#f3f4f6', '#fee2e2', '#fef3c7', '#dcfce7', '#d1fae5', '#e0f2fe', '#e0e7ff', '#ede9fe', '#fae8ff', '#f5f5f5'];

  useEffect(() => {
    const loadBoard = async () => {
      try {
        const data = await fetchBoardData(boardId);
        setBoard(data);
        setActiveView(data.defaultView || 'kanban');
        const templatesRes = await axios.get('/api/templates', { headers: { 'x-auth-token': token } });
        setTemplates(templatesRes.data);
      } catch (error) {
        console.error('Error loading board:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadBoard();
  }, [boardId, token]);

  // Real-time socket listeners
  useEffect(() => {
    if (!socket || !board) return;

    const boardMongoId = board._id;
    socket.emit('join:board', boardMongoId);

    // Re-join the board room after any (re)connect so transient socket drops don't
    // leave the board silently stale (e.g. after the AI assistant makes changes).
    const onConnect = () => socket.emit('join:board', boardMongoId);
    socket.on('connect', onConnect);

    const onCardCreated = (card) => {
      setBoard(prev => {
        if (!prev || prev.cards.some(c => c._id === card._id)) return prev;
        return { ...prev, cards: [card, ...prev.cards] };
      });
    };

    const onCardUpdated = (card) => {
      setBoard(prev => {
        if (!prev) return prev;
        return { ...prev, cards: prev.cards.map(c => c._id === card._id ? { ...c, ...card } : c) };
      });
    };

    const onCardDeleted = ({ cardId }) => {
      setBoard(prev => {
        if (!prev) return prev;
        return { ...prev, cards: prev.cards.filter(c => c._id !== cardId) };
      });
    };

    const onBoardUpdated = (update) => {
      setBoard(prev => prev ? { ...prev, ...update } : prev);
    };

    socket.on('card:created', onCardCreated);
    socket.on('card:updated', onCardUpdated);
    socket.on('card:deleted', onCardDeleted);
    socket.on('board:updated', onBoardUpdated);

    return () => {
      socket.emit('leave:board', boardMongoId);
      socket.off('connect', onConnect);
      socket.off('card:created', onCardCreated);
      socket.off('card:updated', onCardUpdated);
      socket.off('card:deleted', onCardDeleted);
      socket.off('board:updated', onBoardUpdated);
    };
  }, [socket, board?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateBoardSettings = async (update) => {
    try {
      console.log('Updating board settings:', update);
      const res = await axios.put(`/api/boards/${board._id}`, update, {
        headers: { 'x-auth-token': token }
      });
      console.log('Board update response:', res.data);
      // Merge the new board data but keep existing cards to avoid blank screen
      setBoard(prev => ({ ...prev, ...res.data, cards: prev.cards }));

    } catch (error) {
      console.error('Error updating board settings:', error);
      console.error('Error details:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.msg || error.message || 'Unknown error';
      alert(`Failed to save board settings: ${errorMsg}`);
    }
  };

  const handleCreateKnowledgeFile = async () => {
    try {
      const config = { headers: { 'x-auth-token': token } };
      const res = await axios.post(`/api/boards/${board._id}/knowledge`, { name: 'untitled.md', content: '' }, config);
      const newFile = res.data;
      setBoard(prev => ({ ...prev, knowledgeFiles: [...(prev.knowledgeFiles || []), newFile] }));
      setEditingKnowledgeFile(newFile);
    } catch (err) {
      console.error('Error creating knowledge file:', err);
    }
  };

  const handleSaveKnowledgeFile = async (fileId, name, content) => {
    try {
      const config = { headers: { 'x-auth-token': token } };
      const res = await axios.put(`/api/boards/${board._id}/knowledge/${fileId}`, { name, content }, config);
      setBoard(prev => ({
        ...prev,
        knowledgeFiles: prev.knowledgeFiles.map(f => f._id === fileId ? { ...f, name: res.data.name, content: res.data.content } : f)
      }));
      setEditingKnowledgeFile(prev => prev?._id === fileId ? { ...prev, name: res.data.name, content: res.data.content } : prev);
    } catch (err) {
      console.error('Error saving knowledge file:', err);
      throw err;
    }
  };

  const handleDeleteKnowledgeFile = async (fileId) => {
    if (!window.confirm('Delete this knowledge file?')) return;
    try {
      const config = { headers: { 'x-auth-token': token } };
      await axios.delete(`/api/boards/${board._id}/knowledge/${fileId}`, config);
      setBoard(prev => ({ ...prev, knowledgeFiles: prev.knowledgeFiles.filter(f => f._id !== fileId) }));
      if (editingKnowledgeFile?._id === fileId) setEditingKnowledgeFile(null);
    } catch (err) {
      console.error('Error deleting knowledge file:', err);
    }
  };

  const handleColumnStyleUpdate = async (columnName, style) => {
    const currentSettings = board.columnSettings || [];
    const index = currentSettings.findIndex(s => s.columnName === columnName);
    
    let newSettings = [...currentSettings];
    if (index > -1) {
      newSettings[index] = { ...newSettings[index], ...style };
    } else {
      newSettings.push({ columnName, ...style });
    }

    await updateBoardSettings({ columnSettings: newSettings });
    setEditingColumnSettings(null);
  };

  const handleAddColumn = async () => {
    if (!newColumnName.trim()) return;
    try {
      const updatedColumns = [...board.columns, newColumnName.trim()];
      const res = await axios.put(`/api/boards/${board._id}`, 
        { columns: updatedColumns },
        { headers: { 'x-auth-token': token } }
      );
      setBoard({ ...board, columns: res.data.columns });
      setNewColumnName('');
      setIsAddingColumn(false);
    } catch (error) {
      console.error('Error adding column:', error);
    }
  };

  const handleRenameColumn = async (oldName) => {
    if (!newColumnName.trim() || oldName === newColumnName.trim()) {
      setEditingColumn(null);
      return;
    }
    
    try {
      const updatedColumns = board.columns.map(c => c === oldName ? newColumnName.trim() : c);
      const updatedSettings = (board.columnSettings || []).map(s => 
        s.columnName === oldName ? { ...s, columnName: newColumnName.trim() } : s
      );
      
      // Update local cards so they don't "disappear" from the UI while saving
      const updatedCards = (board.cards || []).map(card => 
        card.column === oldName ? { ...card, column: newColumnName.trim() } : card
      );

      await axios.put(`/api/boards/${board._id}`, 
        { columns: updatedColumns, columnSettings: updatedSettings },
        { headers: { 'x-auth-token': token } }
      );

      setBoard({ ...board, columns: updatedColumns, columnSettings: updatedSettings, cards: updatedCards });
      setEditingColumn(null);
      setNewColumnName('');
      
      const data = await fetchBoardData(boardId);
      setBoard(data);
    } catch (error) {
      console.error('Error renaming column:', error);
    }
  };

  const handleAddCard = async (e) => {
    e.preventDefault();
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      const body = {
        ...newCardData,
        boardId: board._id
      };
      
      const res = await axios.post('/api/cards', body, config);

      // The card:created socket broadcast may land before this response resolves
      // (we're in the board room too), so skip the insert if it's already there.
      setBoard(prevBoard => {
        if (prevBoard.cards.some(c => c._id === res.data._id)) return prevBoard;
        return { ...prevBoard, cards: [res.data, ...prevBoard.cards] };
      });
      
      setShowAddModal(false);
      setShowAssigneeList(false);
      setAiQuestions('');
      setShowSaveTemplateForm(false);
      setSaveTemplateName('');
      setSaveTemplateSuccess(false);
      setNewCardData({
        title: '',
        description: '',
        priority: 'Medium',
        column: '',
        cardType: 'task',
        labels: [],
        contactInfo: {
          companyName: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          website: '',
          address: '',
          jobTitle: ''
        },
        socialMedia: {
          platforms: [],
          postCopy: '',
          platformCopy: {
            facebook: '',
            linkedin: '',
            x: ''
          },
          ctaLink: '',
          scheduledDate: ''
        }
      });
    } catch (error) {
      console.error('Error adding card:', error.response?.data || error.message);
      alert(error.response?.data?.msg || 'Failed to add card. Please try again.');
    }
  };

  const fetchArchivedCards = async () => {
    setLoadingArchived(true);
    try {
      const res = await axios.get(`/api/cards/board/${board._id}?archived=true`, {
        headers: { 'x-auth-token': token }
      });
      setArchivedCards(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingArchived(false);
    }
  };

  const handleRestoreCard = async (id) => {
    try {
      await axios.put(`/api/cards/${id}`, { isArchived: false }, {
        headers: { 'x-auth-token': token }
      });
      setArchivedCards(archivedCards.filter(c => c._id !== id));
      const data = await fetchBoardData(boardId);
      setBoard(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteCard = async (id) => {
    if (!window.confirm('Permanently delete this card?')) return;
    try {
      await axios.delete(`/api/cards/${id}`, {
        headers: { 'x-auth-token': token }
      });
      setArchivedCards(archivedCards.filter(c => c._id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleArchiveBoard = async (archive) => {
    try {
      await axios.put(`/api/boards/${board._id}`, { isArchived: archive }, {
        headers: { 'x-auth-token': token }
      });
      if (archive) window.location.href = '/dashboard';
      else setBoard(prev => ({ ...prev, isArchived: false }));
    } catch (err) {
      alert('Failed to update the board archive state');
    }
  };

  const handleDeleteBoard = async () => {
    if (deleteBoardName !== board.name) return;
    try {
      await axios.delete(`/api/boards/${board._id}`, {
        headers: { 'x-auth-token': token }
      });
      window.location.href = '/dashboard';
    } catch (err) {
      alert('Failed to delete board');
    }
  };

  const handleTemplateSelect = (template) => {
    if (!template) {
      // Clear selection — reset description and priority
      setNewCardData({
        ...newCardData,
        description: '',
        priority: 'Medium'
      });
      return;
    }
    setNewCardData({
      ...newCardData,
      description: template.descriptionTemplate,
      priority: template.defaultPriority || 'Medium'
    });
  };

  const handleSaveAsTemplate = async (templateName) => {
    if (!templateName.trim()) return;
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post('/api/templates', {
        name: templateName.trim(),
        descriptionTemplate: newCardData.description,
        defaultPriority: newCardData.priority,
      }, { headers: { 'x-auth-token': token } });
      setTemplates(prev => [...prev, res.data]);
      setSaveTemplateSuccess(true);
      setSaveTemplateName('');
      setTimeout(() => {
        setSaveTemplateSuccess(false);
        setShowSaveTemplateForm(false);
      }, 1800);
    } catch (err) {
      console.error('Failed to save template:', err);
      alert('Failed to save template');
    }
  };

  const handleAiEnhance = async () => {
    if (!newCardData.title) return alert('Please enter a title first');
    setAiLoading(true);
    setAiQuestions('');
    try {
      const res = await axios.post('/api/ai/enhance', {
        title: newCardData.title,
        description: newCardData.description
      }, { headers: { 'x-auth-token': token } });
      setAiQuestions(res.data.questions);
    } catch (err) {
      console.error(err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiFormulate = async () => {
    if (!userAiResponse.trim()) return alert('Please provide some answers for the AI');
    setAiLoading(true);
    try {
      const res = await axios.post('/api/ai/formulate', {
        title: newCardData.title,
        originalDescription: newCardData.description,
        questions: aiQuestions,
        userAnswers: userAiResponse
      }, { headers: { 'x-auth-token': token } });
      
      setNewCardData({ ...newCardData, description: res.data.finalizedDescription });
      setAiQuestions('');
      setUserAiResponse('');
    } catch (err) {
      console.error(err);
    } finally {
      setAiLoading(false);
    }
  };

  // Inline-edit helpers for the table view (field patches reuse patchCard below).
  const mergeCardResponse = (res) => {
    setBoard(prev => ({ ...prev, cards: prev.cards.map(c => c._id === res.data._id ? { ...c, ...res.data } : c) }));
  };

  const toggleSubtaskInline = async (cardId, subtaskId) => {
    setBoard(prev => ({
      ...prev,
      cards: prev.cards.map(c => c._id !== cardId ? c : {
        ...c,
        subtasks: (c.subtasks || []).map(s => s._id === subtaskId ? { ...s, completed: !s.completed } : s)
      })
    }));
    try {
      const res = await axios.put(`/api/cards/${cardId}/subtasks/${subtaskId}/toggle`, {}, { headers: { 'x-auth-token': token } });
      mergeCardResponse(res);
    } catch (err) {
      console.error('Error toggling subtask:', err);
    }
  };

  const addSubtaskInline = async (cardId, content) => {
    try {
      const res = await axios.post(`/api/cards/${cardId}/subtasks`, { content }, { headers: { 'x-auth-token': token } });
      mergeCardResponse(res);
    } catch (err) {
      console.error('Error adding subtask:', err);
    }
  };

  // Title-only card creation (table view "+ Add item" / "+ Add sub-item").
  const quickAddCard = async (column, title, parentId = null) => {
    try {
      const body = { title, column, boardId: board._id, priority: 'Medium', cardType: 'task' };
      if (parentId) body.parent = parentId;
      const res = await axios.post('/api/cards', body, { headers: { 'x-auth-token': token } });
      setBoard(prev => {
        if (prev.cards.some(c => c._id === res.data._id)) return prev;
        return { ...prev, cards: [res.data, ...prev.cards] };
      });
    } catch (err) {
      console.error('Error creating card:', err);
      alert(err.response?.data?.msg || 'Failed to create card');
    }
  };

  const openAddModal = (columnName) => {
    setNewCardData({
      title: '',
      description: '',
      priority: 'Medium',
      column: columnName || (board.columns && board.columns[0]) || '',
      cardType: 'task',
      assignedTo: [],
      labels: [],
      contactInfo: {
        companyName: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        jobTitle: ''
      },
      socialMedia: {
        platforms: [],
        postCopy: '',
        platformCopy: {
          facebook: '',
          linkedin: '',
          x: ''
        },
        ctaLink: '',
        scheduledDate: ''
      }
    });
    setShowAssigneeList(false);
    setShowAddModal(true);
  };
  
  const toggleBulkMode = () => {
    setBulkMode(prev => !prev);
    setSelectedCards(new Set());
    setBulkMoveTarget('');
    setBulkPriorityTarget('');
    setShowBulkAssignDropdown(false);
  };

  const toggleCardSelection = (cardId) => {
    setSelectedCards(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const handleBulkMove = async (targetColumn) => {
    const ids = [...selectedCards];
    await Promise.all(ids.map(id =>
      axios.put(`/api/cards/${id}`, { column: targetColumn }, { headers: { 'x-auth-token': token } })
    ));
    setBoard(prev => ({
      ...prev,
      cards: prev.cards.map(c => selectedCards.has(c._id) ? { ...c, column: targetColumn } : c)
    }));
    setBulkMoveTarget('');
  };

  const handleBulkArchive = async () => {
    const ids = [...selectedCards];
    await Promise.all(ids.map(id =>
      axios.put(`/api/cards/${id}`, { isArchived: true }, { headers: { 'x-auth-token': token } })
    ));
    setBoard(prev => ({
      ...prev,
      cards: prev.cards.filter(c => !selectedCards.has(c._id))
    }));
    setSelectedCards(new Set());
    setBulkMode(false);
  };

  const handleBulkSetPriority = async (priority) => {
    const ids = [...selectedCards];
    await Promise.all(ids.map(id =>
      axios.put(`/api/cards/${id}`, { priority }, { headers: { 'x-auth-token': token } })
    ));
    setBoard(prev => ({
      ...prev,
      cards: prev.cards.map(c => selectedCards.has(c._id) ? { ...c, priority } : c)
    }));
    setBulkPriorityTarget('');
  };

  const handleBulkAssign = async (userId) => {
    const ids = [...selectedCards];
    await Promise.all(ids.map(id =>
      axios.put(`/api/cards/${id}`,
        { assignedTo: [...(board.cards.find(c => c._id === id)?.assignedTo?.map(u => u._id || u) || []), userId] },
        { headers: { 'x-auth-token': token } }
      )
    ));
    setBoard(prev => ({
      ...prev,
      cards: prev.cards.map(c => {
        if (!selectedCards.has(c._id)) return c;
        const already = c.assignedTo?.some(u => (u._id || u) === userId);
        if (already) return c;
        const userObj = getCollaborators().find(u => u._id === userId);
        return { ...c, assignedTo: [...(c.assignedTo || []), userObj] };
      })
    }));
    setShowBulkAssignDropdown(false);
  };

  const openConsolidateModal = () => {
    const selected = board.cards.filter(c => selectedCards.has(c._id));
    if (selected.length < 2) return;
    const priorityRank = { Urgent: 4, High: 3, Medium: 2, Low: 1 };
    const topPriority = selected.reduce((acc, c) => (priorityRank[c.priority] > priorityRank[acc] ? c.priority : acc), 'Low');
    const latestDue = selected.reduce((acc, c) => {
      if (!c.dueDate) return acc;
      const t = new Date(c.dueDate).getTime();
      return t > acc ? t : acc;
    }, 0);
    const cols = new Set(selected.map(c => c.column));
    const defaultCol = cols.size === 1 ? [...cols][0] : (board.columns[0] || '');
    setConsolidateForm({
      title: '',
      priority: topPriority,
      dueDate: latestDue ? new Date(latestDue).toISOString().slice(0, 10) : '',
      column: defaultCol,
      originalsAction: 'archive'
    });
    setShowConsolidateModal(true);
  };

  const submitConsolidate = async () => {
    if (!consolidateForm.title.trim()) return;
    setConsolidating(true);
    try {
      const ids = [...selectedCards];
      const res = await axios.post('/api/cards/consolidate', {
        boardId: board._id,
        cardIds: ids,
        title: consolidateForm.title.trim(),
        priority: consolidateForm.priority,
        dueDate: consolidateForm.dueDate || null,
        column: consolidateForm.column,
        originalsAction: consolidateForm.originalsAction
      }, { headers: { 'x-auth-token': token } });

      setBoard(prev => {
        let cards = prev.cards;
        if (consolidateForm.originalsAction === 'delete') {
          cards = cards.filter(c => !selectedCards.has(c._id));
        } else if (consolidateForm.originalsAction === 'archive') {
          cards = cards.filter(c => !selectedCards.has(c._id));
        }
        if (!cards.some(c => c._id === res.data.parent._id)) {
          cards = [...cards, res.data.parent];
        }
        return { ...prev, cards };
      });
      setShowConsolidateModal(false);
      setSelectedCards(new Set());
      setBulkMode(false);
    } catch (err) {
      console.error('Consolidate error:', err);
      alert(err.response?.data?.msg || 'Failed to consolidate cards');
    } finally {
      setConsolidating(false);
    }
  };

  const handleDragEnd = async (result) => {
    dragActiveRef.current = false;
    if (kanbanZoomRef.current) kanbanZoomRef.current.style.zoom = String(boardZoomRef.current);

    const { destination, source, draggableId, type } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Handle column reordering
    if (type === 'column') {
      const newColumns = Array.from(board.columns);
      const [moved] = newColumns.splice(source.index, 1);
      newColumns.splice(destination.index, 0, moved);

      // Optimistic update
      setBoard(prev => ({ ...prev, columns: newColumns }));

      try {
        await axios.put(
          `/api/boards/${board._id}`,
          { columns: newColumns },
          { headers: { 'x-auth-token': token } }
        );
      } catch (error) {
        console.error('Error reordering columns:', error);
        setBoard(prev => ({ ...prev, columns: board.columns }));
      }
      return;
    }

    const destColumn = destination.droppableId;
    const destIndex = destination.index;

    // Derive neighbors from the displayed (filtered, manually-ordered) column so the
    // computed order matches where the user actually dropped the card. Reordering is
    // disabled while an explicit sort is active (see isDragDisabled below).
    const destCards = (cardsByColumn[destColumn] || []).filter(c => c._id !== draggableId);

    // Columns with a top/bottom placement setting snap cards dragged in from
    // another column to that end, ignoring the drop point. Reordering within
    // the same column stays manual.
    const placement = board.columnSettings?.find(s => s.columnName === destColumn)?.cardPlacement || 'manual';
    const isCrossColumn = source.droppableId !== destColumn;

    let newOrder;
    if (isCrossColumn && placement === 'top') {
      newOrder = (destCards[0]?.order ?? 1000) - 1000;
    } else if (isCrossColumn && placement === 'bottom') {
      newOrder = (destCards[destCards.length - 1]?.order ?? -1000) + 1000;
    } else {
      // Midpoint logic: land exactly where dropped
      const before = destCards[destIndex - 1];
      const after = destCards[destIndex];

      if (!before && !after) {
        newOrder = 1000;
      } else if (!before) {
        newOrder = (after.order ?? 0) - 500;
      } else if (!after) {
        newOrder = (before.order ?? 0) + 1000;
      } else {
        newOrder = ((before.order ?? 0) + (after.order ?? 0)) / 2;
      }
    }

    // Optimistic update
    const updatedCards = board.cards.map(card =>
      card._id === draggableId
        ? { ...card, column: destColumn, order: newOrder }
        : card
    );
    setBoard(prev => ({ ...prev, cards: updatedCards }));

    // Persist to backend
    try {
      await axios.put(
        `/api/cards/${draggableId}`,
        { column: destColumn, order: newOrder },
        { headers: { 'x-auth-token': token } }
      );
    } catch (error) {
      console.error('Error updating card position:', error);
      // Revert optimistic update on failure
      setBoard(prev => ({ ...prev, cards: board.cards }));
    }
  };

  // Optimistically apply field changes to one card, persist, reconcile with the
  // server copy (or revert on failure). Shared by the right-click quick actions.
  const patchCard = (cardId, fields) => {
    const prevCards = board.cards;
    setBoard(prev => ({ ...prev, cards: prev.cards.map(c => c._id === cardId ? { ...c, ...fields } : c) }));
    return axios.put(`/api/cards/${cardId}`, fields, { headers: { 'x-auth-token': token } })
      .then(res => setBoard(prev => ({
        ...prev,
        cards: prev.cards.map(c => c._id === cardId ? { ...c, ...res.data } : c)
      })))
      .catch(err => {
        console.error('Error updating card:', err);
        setBoard(prev => ({ ...prev, cards: prevCards }));
      });
  };

  // Context-menu "Move to": no drop point, so land per the destination column's
  // placement setting ('top' snaps first; 'bottom' and 'manual' append last).
  const handleQuickMove = (card, destColumn) => {
    const destCards = cardsByColumn[destColumn] || [];
    const placement = board.columnSettings?.find(s => s.columnName === destColumn)?.cardPlacement || 'manual';
    const newOrder = placement === 'top'
      ? (destCards[0]?.order ?? 1000) - 1000
      : (destCards[destCards.length - 1]?.order ?? -1000) + 1000;
    patchCard(card._id, { column: destColumn, order: newOrder });
  };

  const handleQuickToggleLabel = (card, label) => {
    const has = (card.labels || []).some(l => l.text === label.text);
    const labels = has
      ? (card.labels || []).filter(l => l.text !== label.text)
      : [...(card.labels || []), { text: label.text, color: label.color }];
    patchCard(card._id, { labels });
  };

  const handleQuickToggleComplete = (card) => {
    patchCard(card._id, { completedAt: card.completedAt ? null : new Date().toISOString() });
  };

  const handleQuickArchive = (card) => {
    const prevCards = board.cards;
    setBoard(prev => ({ ...prev, cards: prev.cards.filter(c => c._id !== card._id) }));
    axios.put(`/api/cards/${card._id}`, { isArchived: true }, { headers: { 'x-auth-token': token } })
      .catch(err => {
        console.error('Error archiving card:', err);
        setBoard(prev => ({ ...prev, cards: prevCards }));
      });
  };


  const getCollaborators = () => {
    if (!board) return [];
    const collaborators = new Map();

    // Add owner
    if (board.owner) {
      const ownerId = board.owner._id || board.owner;
      collaborators.set(ownerId.toString(), board.owner);
    }

    // Add board members
    if (board.members) {
      board.members.forEach(m => {
        if (m.user) {
          const userId = m.user._id || m.user;
          collaborators.set(userId.toString(), m.user);
        }
      });
    }

    // Add workspace members
    if (board.workspace && board.workspace.members) {
      board.workspace.members.forEach(m => {
        if (m.user) {
          const userId = m.user._id || m.user;
          collaborators.set(userId.toString(), m.user);
        }
      });
    }

    // Remove current user from the list
    if (currentUser) {
      collaborators.delete(currentUser._id.toString());
    }

    return Array.from(collaborators.values()).filter(c => typeof c === 'object');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-blue"></div>
      </div>
    );
  }

  if (!board) {
    return <div className="text-center p-8 text-gray-500 dark:text-gray-400">Board not found.</div>;
  }

  if (board.type === 'marketing') {
    return <MarketingView board={board} token={token} currentUser={currentUser} />;
  }

  // Group children by their parent id so parent cards can render them inline.
  const childrenByParent = board.cards.reduce((acc, c) => {
    const parentId = c.parent && (typeof c.parent === 'object' ? c.parent._id : c.parent);
    if (parentId) {
      if (!acc[parentId]) acc[parentId] = [];
      acc[parentId].push(c);
    }
    return acc;
  }, {});

  const matchedIds = new Set(filteredCards.map(c => c._id));
  const hasActiveFilter = !!(filters.q || filters.assignees.length || filters.labels.length || filters.tags.length || filters.priorities.length || filters.due);

  const cardsByColumn = board.columns.reduce((acc, column) => {
    // Every card renders in its own column (children included — linking to a parent
    // doesn't pull a card out of its lane). A card also shows if a child matches the
    // filter, so matching sub-cards are not lost when their parent doesn't match.
    const cols = board.cards.filter(card =>
      card.column === column &&
      (matchedIds.has(card._id) || (childrenByParent[card._id] || []).some(ch => matchedIds.has(ch._id)))
    );
    acc[column] = sortCards(cols, filters.sort);
    return acc;
  }, {});

  const topLevelTotal = board.cards.length;
  const filteredVisibleCount = Object.values(cardsByColumn).reduce((n, arr) => n + arr.length, 0);

  const isDarkMode = theme === 'dark';
  const boardStyle = board?.background?.type === 'image'
    ? { backgroundImage: `url(${getFileUrl(board.background.value)})`, backgroundSize: 'cover', backgroundAttachment: 'fixed', backgroundPosition: 'center' }
    : isDarkMode
      ? { backgroundColor: '#111827' }
      : { backgroundColor: board?.background?.value || '#f3f4f6' };

  const canEditBoard = isAdmin || (currentUser && board && (
    (board.owner && (board.owner._id || board.owner).toString() === currentUser._id) ||
    board.members.some(m => m.user && (m.user._id || m.user).toString() === currentUser._id && m.role === 'admin') ||
    (board.workspace && board.workspace.owner && board.workspace.owner.toString() === currentUser._id) ||
    (board.workspace && board.workspace.members && board.workspace.members.some(m => m.user && m.user.toString() === currentUser._id && m.role === 'admin'))
  ));

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col p-6 transition-all duration-500 overflow-hidden dark:bg-gray-900" style={boardStyle}>
      <div className="flex justify-between items-center mb-6 flex-shrink-0">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm px-3 py-1 rounded-lg shadow-sm border border-white/50 dark:border-gray-700/50">
          {board.name}
        </h1>

        <div className="hidden md:flex items-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg p-1 shadow-sm border border-white/50 dark:border-gray-700/50">
          {VIEW_OPTIONS.map(v => (
            <button
              key={v.id}
              onClick={() => setActiveView(v.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                activeView === v.id
                  ? 'bg-primary-blue text-white shadow'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={v.title}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={v.icon} /></svg>
              {v.label}
            </button>
          ))}
        </div>
        
        <div className="flex items-center space-x-4">
          <BoardMapsMenu boardId={board._id} />
          <BoardFlowsMenu boardId={board._id} />
          <Link
            to={`/boards/${board.slug || board._id}/flow`}
            className="px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="How cards move through this board"
          >
            Card Flow
          </Link>
          {board._id === GTD_BOARD_ID && (
            <button
              onClick={() => setShowTickler(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-600 dark:text-gray-300 rounded-full hover:bg-white dark:hover:bg-gray-800 shadow-sm border border-white dark:border-gray-700 transition-all text-sm font-bold"
              title="Show tickler (dated reminders)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              Tickler
            </button>
          )}
          <button
            onClick={async () => {
              setShowSettings(true);
              fetchArchivedCards();
              // One-time migration: if old knowledgeBase exists and no files yet, migrate
              if (board.knowledgeBase?.trim() && !(board.knowledgeFiles?.length > 0)) {
                try {
                  const config = { headers: { 'x-auth-token': token } };
                  const res = await axios.post(`/api/boards/${board._id}/knowledge`, { name: 'knowledge.md', content: board.knowledgeBase }, config);
                  await axios.put(`/api/boards/${board._id}`, { knowledgeBase: '' }, config);
                  setBoard(prev => ({ ...prev, knowledgeBase: '', knowledgeFiles: [...(prev.knowledgeFiles || []), res.data] }));
                } catch (err) {
                  console.error('Migration error:', err);
                }
              }
            }}
            className="p-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-600 dark:text-gray-400 rounded-full hover:bg-white dark:hover:bg-gray-800 shadow-sm border border-white dark:border-gray-700 transition-all"
            title="Board Settings"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
          </button>
          
          <button
            onClick={toggleBulkMode}
            className={`p-2 backdrop-blur-sm rounded-full shadow-sm border transition-all ${bulkMode ? 'bg-blue-600 text-white border-blue-600' : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 border-white dark:border-gray-700'}`}
            title={bulkMode ? 'Exit Bulk Select' : 'Bulk Select'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
            {bulkMode && selectedCards.size > 0 && (
              <span className="ml-1 text-xs font-bold">{selectedCards.size}</span>
            )}
          </button>

          {isAdmin && (
            <div className="flex space-x-4">
              <button
                onClick={() => openAddModal()}
                className="px-4 py-2 bg-primary-blue text-white rounded-md hover:bg-blue-700 shadow-md font-bold text-sm transition-all"
              >
                Add Card
              </button>
            </div>
          )}
        </div>
      </div>

      <FilterBar
        filters={filters}
        setFilters={setFilters}
        assigneeOptions={assigneeOptions}
        labelOptions={labelOptions}
        tagOptions={tagOptions}
        currentUser={currentUser}
        totalCount={topLevelTotal}
        filteredCount={filteredVisibleCount}
      />

      {bulkMode && selectedCards.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 rounded-full shadow-2xl border border-gray-200 dark:border-gray-700">
          <span className="text-sm font-bold text-blue-700 dark:text-blue-400 pr-2 border-r border-gray-200 dark:border-gray-700">
            {selectedCards.size} selected
          </span>
          <select
            value={bulkMoveTarget}
            onChange={(e) => {
              setBulkMoveTarget(e.target.value);
              if (e.target.value) handleBulkMove(e.target.value);
            }}
            className="text-xs border border-blue-200 dark:border-blue-800 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="" disabled>Move to column...</option>
            {board.columns.map(col => <option key={col} value={col}>{col}</option>)}
          </select>
          <select
            value={bulkPriorityTarget}
            onChange={(e) => {
              setBulkPriorityTarget(e.target.value);
              if (e.target.value) handleBulkSetPriority(e.target.value);
            }}
            className="text-xs border border-blue-200 dark:border-blue-800 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="" disabled>Set priority...</option>
            {['Low','Medium','High','Urgent'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className="relative" ref={assignDropdownRef}>
            <button
              onClick={() => setShowBulkAssignDropdown(p => !p)}
              className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-bold"
            >
              Assign...
            </button>
            {showBulkAssignDropdown && (
              <div className="absolute bottom-full mb-2 left-0 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-2 w-48 z-50">
                {getCollaborators().map(user => (
                  <button
                    key={user._id}
                    onClick={() => handleBulkAssign(user._id)}
                    className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-100 rounded transition-colors"
                  >
                    {user.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedCards.size >= 2 && (
            <button
              onClick={openConsolidateModal}
              className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 font-bold"
              title="Combine selected cards into one parent with subtasks"
            >
              Consolidate
            </button>
          )}
          <button
            onClick={handleBulkArchive}
            className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 font-bold"
          >
            Archive
          </button>
          <button
            onClick={() => setSelectedCards(new Set())}
            className="text-xs px-3 py-1 text-gray-500 hover:text-gray-700 font-medium"
          >
            Clear
          </button>
          <button
            onClick={toggleBulkMode}
            className="text-xs px-3 py-1 text-gray-500 hover:text-gray-700 font-medium"
          >
            Exit
          </button>
        </div>
      )}

      {activeView === 'kanban' ? (
        <DragDropContext onDragEnd={handleDragEnd} onBeforeCapture={handleBeforeCapture}>
          <Droppable droppableId="board-columns" direction="horizontal" type="column">
            {(boardProvided) => (
              <div className="flex-1 relative flex flex-col min-h-0">
                {boardZoom !== 1 && (
                  <button
                    onClick={() => setBoardZoom(1)}
                    className="absolute bottom-6 right-2 z-20 px-3 py-1.5 text-xs font-bold rounded-full shadow-md bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:text-primary-blue"
                    title="Reset zoom to 100%"
                  >
                    {Math.round(boardZoom * 100)}%
                  </button>
                )}
                <div
                  ref={kanbanScrollRef}
                  className="flex-1 overflow-auto pb-4 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent"
                >
                  <div
                    ref={(el) => { boardProvided.innerRef(el); kanbanZoomRef.current = el; }}
                    {...boardProvided.droppableProps}
                    className="flex space-x-4 items-start min-w-max"
                    style={{ zoom: boardZoom }}
                  >
                {board.columns.map((column, colIndex) => {
                  const settings = board.columnSettings?.find(s => s.columnName === column);
                  const isDark = theme === 'dark';
                  const colBg = settings?.backgroundColor || '#f3f4f6';
                  const colStyle = isDark ? { backgroundColor: '#1f2937' } : { backgroundColor: colBg };

                  return (
                    <Draggable key={column} draggableId={`column-${column}`} index={colIndex}>
                      {(colProvided, colSnapshot) => (
                        <div
                          ref={colProvided.innerRef}
                          {...colProvided.draggableProps}
                          className={`rounded-lg shadow-md flex flex-col w-72 flex-shrink-0 transition-all border ${isDark ? 'border-gray-700' : 'border-black/5'} ${colSnapshot.isDragging ? 'shadow-xl ring-2 ring-primary-blue/50 rotate-[2deg]' : ''}`}
                          style={{ ...colStyle, ...colProvided.draggableProps.style }}
                        >
                          <div
                            {...colProvided.dragHandleProps}
                            className={`px-4 py-3 border-b ${isDark ? 'border-gray-600' : 'border-black/5'} font-bold ${isDark ? 'text-gray-100' : 'text-gray-800'} flex justify-between items-center group/header relative cursor-grab active:cursor-grabbing`}
                            style={!isDark && settings?.backgroundColor ? { borderBottomColor: 'rgba(0,0,0,0.08)' } : {}}
                          >
                            {editingColumn === column ? (
                              <div className="flex items-center w-full">
                                <input
                                  autoFocus
                                  value={newColumnName}
                                  onChange={(e) => setNewColumnName(e.target.value)}
                                  onBlur={() => handleRenameColumn(column)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleRenameColumn(column)}
                                  className="w-full text-sm border-gray-300 dark:border-gray-600 rounded focus:ring-primary-blue focus:border-primary-blue px-1 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center min-w-0 mr-2">
                                  {settings?.icon && <span className="mr-2 text-lg">{settings.icon}</span>}
                                  <span
                                    className="cursor-pointer hover:text-primary-blue truncate"
                                    onClick={() => {
                                      setEditingColumn(column);
                                      setNewColumnName(column);
                                    }}
                                  >
                                    {column}
                                  </span>
                                  <span className={`ml-2 text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'} font-normal`}>({cardsByColumn[column]?.length || 0})</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={() => setEditingColumnSettings(column)}
                                    className={`p-1 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} opacity-0 group-hover/header:opacity-100 transition-opacity`}
                                    title="Column Style"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.172-1.172a4 4 0 115.656 5.656L10 17.657l-6.828-6.829 6.828-6.828z"></path></svg>
                                  </button>
                                  <button
                                    onClick={() => openAddModal(column)}
                                    className={`p-1 ${isDark ? 'text-gray-500' : 'text-gray-400'} hover:text-primary-blue opacity-0 group-hover/header:opacity-100 transition-opacity`}
                                    title="Add card"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </div>
                              </>
                            )}

                            {/* Column Style Popup Overlay */}
                            {editingColumnSettings === column && (
                              <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-20 border border-gray-200 dark:border-gray-700 p-3" onMouseLeave={() => setEditingColumnSettings(null)}>
                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Column Style</h4>
                                <div className="space-y-3">
                                  <div>
                                    <p className="text-[10px] text-gray-400 mb-1">Background Color</p>
                                    <div className="flex flex-wrap gap-1">
                                      {themeColors.map(c => (
                                        <button
                                          key={c}
                                          onClick={() => handleColumnStyleUpdate(column, { backgroundColor: c })}
                                          className="w-5 h-5 rounded-sm border border-gray-200 dark:border-gray-600"
                                          style={{ backgroundColor: c }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-gray-400 mb-1">Icon</p>
                                    <div className="flex flex-wrap gap-1">
                                      <button onClick={() => handleColumnStyleUpdate(column, { icon: '' })} className="text-[10px] px-1 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300">None</button>
                                      {availableIcons.map(icon => (
                                        <button
                                          key={icon}
                                          onClick={() => handleColumnStyleUpdate(column, { icon })}
                                          className="w-6 h-6 flex items-center justify-center text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                        >
                                          {icon}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-gray-400 mb-1" title="Where cards land when created here or dragged in from another column">New &amp; incoming cards</p>
                                    <div className="flex gap-1">
                                      {[['manual', 'Where dropped'], ['top', 'Top'], ['bottom', 'Bottom']].map(([value, label]) => {
                                        const active = (settings?.cardPlacement || 'manual') === value;
                                        return (
                                          <button
                                            key={value}
                                            onClick={() => handleColumnStyleUpdate(column, { cardPlacement: value })}
                                            className={`text-[10px] font-bold px-2 py-1 rounded border transition-colors ${active
                                              ? 'bg-primary-blue text-white border-primary-blue'
                                              : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-primary-blue'}`}
                                          >
                                            {label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          <Droppable droppableId={column} type="card">
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className="min-h-[150px] p-3 space-y-2 group/col relative"
                              >
                                {(!cardsByColumn[column] || cardsByColumn[column].length === 0) && (
                                  <button
                                    type="button"
                                    onClick={() => openAddModal(column)}
                                    className="absolute inset-0 m-3 flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 opacity-0 group-hover/col:opacity-100 hover:border-primary-blue hover:text-primary-blue text-gray-400 dark:text-gray-500 transition-opacity"
                                    aria-label={`Add task to ${column}`}
                                  >
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                                  </button>
                                )}
                                {cardsByColumn[column]?.map((card, index) => (
                                  <Draggable
                                    key={card._id}
                                    draggableId={card._id}
                                    index={index}
                                    isDragDisabled={!!filters.sort}
                                  >
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...(bulkMode ? {} : provided.dragHandleProps)}
                                        onClick={bulkMode ? () => toggleCardSelection(card._id) : undefined}
                                        onContextMenu={bulkMode ? undefined : (e) => {
                                          e.preventDefault();
                                          setContextMenu({ x: e.clientX, y: e.clientY, cardId: card._id });
                                        }}
                                      >
                                        <CardItem
                                          card={card}
                                          childCards={(childrenByParent[card._id] || []).filter(ch => !hasActiveFilter || matchedIds.has(ch._id))}
                                          onChildClick={(childId) => setSelectedCardId(childId)}
                                          onClick={bulkMode ? undefined : () => setSelectedCardId(card._id)}
                                          bulkMode={bulkMode}
                                          isSelected={selectedCards.has(card._id)}
                                          onToggleComplete={(cardId, currentCompletedAt) => {
                                            const newVal = currentCompletedAt ? null : new Date().toISOString();
                                            const updatedCards = board.cards.map(c =>
                                              c._id === cardId ? { ...c, completedAt: newVal } : c
                                            );
                                            setBoard(prev => ({ ...prev, cards: updatedCards }));
                                            axios.put(
                                              `/api/cards/${cardId}`,
                                              { completedAt: newVal },
                                              { headers: { 'x-auth-token': token } }
                                            ).then(res => {
                                              setBoard(prev => ({
                                                ...prev,
                                                cards: prev.cards.map(c => c._id === cardId ? { ...c, ...res.data } : c)
                                              }));
                                            }).catch(() => setBoard(prev => ({ ...prev, cards: board.cards })));
                                          }}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {boardProvided.placeholder}

                {/* Add Column Section */}
                <div className="w-72 flex-shrink-0">
                  {isAddingColumn ? (
                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg p-3 shadow-md border border-white dark:border-gray-700">
                      <input
                        autoFocus
                        placeholder="Column name..."
                        value={newColumnName}
                        onChange={(e) => setNewColumnName(e.target.value)}
                        className="w-full text-sm border-gray-300 dark:border-gray-600 rounded focus:ring-primary-blue focus:border-primary-blue px-2 py-1.5 mb-2 dark:bg-gray-700 dark:text-gray-100"
                      />
                      <div className="flex space-x-2">
                        <button onClick={handleAddColumn} className="px-3 py-1 bg-primary-blue text-white text-xs font-bold rounded">Add</button>
                        <button onClick={() => setIsAddingColumn(false)} className="px-3 py-1 text-gray-500 dark:text-gray-400 text-xs hover:underline">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsAddingColumn(true)}
                      className="w-full flex items-center justify-center p-3 bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm border-2 border-dashed border-gray-400/50 dark:border-gray-600/50 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-800/50 hover:border-primary-blue hover:text-primary-blue transition-all group shadow-sm"
                    >
                      <svg className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                      Add another column
                    </button>
                  )}
                  </div>
                  </div>
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : activeView === 'list' ? (
        <ListView
          board={board}
          cardsByColumn={cardsByColumn}
          sortActive={!!filters.sort}
          onCardClick={setSelectedCardId}
          openAddModal={openAddModal}
          bulkMode={bulkMode}
          selectedCards={selectedCards}
          onToggleCard={toggleCardSelection}
          onSelectAll={(cardIds) => {
            setSelectedCards(prev => {
              const next = new Set(prev);
              const allSelected = cardIds.every(id => prev.has(id));
              cardIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
              return next;
            });
          }}
          onReorder={({ cardId, newOrder, column }) => {
            // Optimistic update
            const updatedCards = board.cards.map(card =>
              card._id === cardId
                ? { ...card, column, order: newOrder }
                : card
            );
            setBoard(prev => ({ ...prev, cards: updatedCards }));

            // Persist to backend
            axios.put(
              `/api/cards/${cardId}`,
              { column, order: newOrder },
              { headers: { 'x-auth-token': token } }
            ).catch(error => {
              console.error('Error updating card position:', error);
              setBoard(prev => ({ ...prev, cards: board.cards }));
            });
          }}
          onMoveCard={async (cardId, newColumn) => {
            // Optimistic update
            const updatedCards = board.cards.map(card =>
              card._id === cardId ? { ...card, column: newColumn } : card
            );
            setBoard(prev => ({ ...prev, cards: updatedCards }));

            // Persist to backend
            try {
              await axios.put(
                `/api/cards/${cardId}`,
                { column: newColumn },
                { headers: { 'x-auth-token': token } }
              );
            } catch (error) {
              console.error('Error moving card to column:', error);
              setBoard(prev => ({ ...prev, cards: board.cards }));
            }
          }}
          onToggleComplete={(cardId, currentCompletedAt) => {
            const newVal = currentCompletedAt ? null : new Date().toISOString();
            const updatedCards = board.cards.map(c =>
              c._id === cardId ? { ...c, completedAt: newVal } : c
            );
            setBoard(prev => ({ ...prev, cards: updatedCards }));
            axios.put(
              `/api/cards/${cardId}`,
              { completedAt: newVal },
              { headers: { 'x-auth-token': token } }
            ).then(res => {
              setBoard(prev => ({
                ...prev,
                cards: prev.cards.map(c => c._id === cardId ? { ...c, ...res.data } : c)
              }));
            }).catch(() => setBoard(prev => ({ ...prev, cards: board.cards })));
          }}
        />
      ) : activeView === 'table' ? (
        <TableView
          board={board}
          cardsByColumn={cardsByColumn}
          childrenByParent={childrenByParent}
          onCardClick={setSelectedCardId}
          openAddModal={openAddModal}
          onQuickAddCard={quickAddCard}
          bulkMode={bulkMode}
          selectedCards={selectedCards}
          onToggleCard={toggleCardSelection}
          onSelectAll={(cardIds) => {
            setSelectedCards(prev => {
              const next = new Set(prev);
              const allSelected = cardIds.every(id => prev.has(id));
              cardIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
              return next;
            });
          }}
          onMoveCard={(cardId, newColumn) => patchCard(cardId, { column: newColumn })}
          onUpdateCard={patchCard}
          onToggleComplete={(cardId, currentCompletedAt) => patchCard(cardId, { completedAt: currentCompletedAt ? null : new Date().toISOString() })}
          onToggleSubtask={toggleSubtaskInline}
          onAddSubtask={addSubtaskInline}
          onAddChildCard={(parentCard, title) => quickAddCard(parentCard.column, title, parentCard._id)}
        />
      ) : (
        <CalendarView
          board={board}
          allCards={filteredCards}
          onCardClick={setSelectedCardId}
        />
      )}

      {/* Right-click quick actions */}
      {contextMenu && (() => {
        const card = board.cards.find(c => c._id === contextMenu.cardId);
        if (!card) return null;
        return (
          <CardContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            card={card}
            columns={board.columns}
            columnSettings={board.columnSettings}
            labelOptions={labelOptions}
            onMove={handleQuickMove}
            onToggleLabel={handleQuickToggleLabel}
            onToggleComplete={handleQuickToggleComplete}
            onArchive={handleQuickArchive}
            onClose={() => setContextMenu(null)}
          />
        );
      })()}

      {/* Card Detail Modal */}
      {selectedCardId && (
        <CardModal
          cardId={selectedCardId}
          onClose={() => setSelectedCardId(null)}
          onNavigateToCard={(id) => setSelectedCardId(id)}
          existingLabels={labelOptions}
          existingTags={tagOptions}
          onUpdate={async () => {
            const data = await fetchBoardData(boardId);
            setBoard(data);
          }}
        />
      )}

      {/* Knowledge File Side Panel */}
      {editingKnowledgeFile && (
        <KnowledgeFileEditor
          file={editingKnowledgeFile}
          onSave={handleSaveKnowledgeFile}
          onClose={() => setEditingKnowledgeFile(null)}
          boardId={board._id}
          token={token}
        />
      )}

      {/* Tickler Panel (GTD board) */}
      {showTickler && <TicklerPanel token={token} onClose={() => setShowTickler(false)} />}

      {/* Board Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowSettings(false)}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Board Settings</h3>
                <button onClick={() => setShowSettings(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* View Type Selection (Moved here for mobile) */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Change View</label>
                  <div className="grid grid-cols-4 gap-2">
                    <button 
                      onClick={() => setActiveView('kanban')}
                      className={`px-3 py-2 text-xs font-bold rounded-md border transition-all ${activeView === 'kanban' ? 'bg-primary-blue text-white border-primary-blue shadow-md' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600'}`}
                    >
                      Kanban
                    </button>
                    <button 
                      onClick={() => setActiveView('list')}
                      className={`px-3 py-2 text-xs font-bold rounded-md border transition-all ${activeView === 'list' ? 'bg-primary-blue text-white border-primary-blue shadow-md' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                    >
                      List
                    </button>
                    <button 
                      onClick={() => setActiveView('table')}
                      className={`px-3 py-2 text-xs font-bold rounded-md border transition-all ${activeView === 'table' ? 'bg-primary-blue text-white border-primary-blue shadow-md' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                    >
                      Table
                    </button>
                    <button 
                      onClick={() => setActiveView('calendar')}
                      className={`px-3 py-2 text-xs font-bold rounded-md border transition-all ${activeView === 'calendar' ? 'bg-primary-blue text-white border-primary-blue shadow-md' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                    >
                      Calendar
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Board Background</label>
                  <div className="grid grid-cols-5 gap-2 mb-4">
                    {themeColors.map(c => (
                      <button 
                        key={c}
                        onClick={() => updateBoardSettings({ background: { type: 'color', value: c } })}
                        className={`h-8 rounded-md border shadow-sm transition-transform hover:scale-105 ${board.background?.value === c ? 'ring-2 ring-primary-blue ring-offset-2' : ''}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Image URL</p>
                    <div className="flex space-x-2">
                      <input 
                        type="text" 
                        placeholder="https://images.unsplash.com/..." 
                        className="flex-1 text-sm border-gray-300 rounded focus:ring-primary-blue focus:border-primary-blue px-2 py-1"
                        onBlur={(e) => e.target.value && updateBoardSettings({ background: { type: 'image', value: e.target.value } })}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Default View</label>
                  <select 
                    value={board.defaultView || 'kanban'}
                    onChange={(e) => updateBoardSettings({ defaultView: e.target.value })}
                    className="w-full text-sm border-gray-300 rounded focus:ring-primary-blue focus:border-primary-blue p-2"
                  >
                    <option value="kanban">Kanban</option>
                    <option value="list">List</option>
                    <option value="table">Table</option>
                    <option value="calendar">Calendar</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-2">When a card is marked complete</label>
                  <div className="flex gap-2">
                    <select
                      value={board.completionBehavior?.action || 'stay'}
                      onChange={(e) => updateBoardSettings({
                        completionBehavior: {
                          action: e.target.value,
                          targetColumn: e.target.value === 'move'
                            ? (board.completionBehavior?.targetColumn || board.columns?.[board.columns.length - 1] || '')
                            : ''
                        }
                      })}
                      className="text-sm border-gray-300 rounded focus:ring-primary-blue focus:border-primary-blue p-2"
                    >
                      <option value="stay">Stay in current column</option>
                      <option value="move">Move to column...</option>
                      <option value="archive">Archive the card</option>
                    </select>
                    {board.completionBehavior?.action === 'move' && (
                      <select
                        value={board.completionBehavior?.targetColumn || ''}
                        onChange={(e) => updateBoardSettings({
                          completionBehavior: { action: 'move', targetColumn: e.target.value }
                        })}
                        className="flex-1 text-sm border-gray-300 rounded focus:ring-primary-blue focus:border-primary-blue p-2"
                      >
                        {(board.columns || []).map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 italic">
                    Fires every time a card is checked off. Unchecking does not undo the action.
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!board.excludeFromMasterList}
                      onChange={(e) => updateBoardSettings({ excludeFromMasterList: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-bold text-gray-700">Exclude from dashboard "All Tasks" list</span>
                  </label>
                  <p className="text-[10px] text-gray-400 mt-1 italic">
                    Hide this board's cards from the master task list. Use for organizing/reference boards that aren't task tracking.
                  </p>
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-bold text-gray-700">Knowledge Files</label>
                    {canEditBoard && (
                      <button
                        onClick={handleCreateKnowledgeFile}
                        className="text-[10px] font-black text-primary-blue hover:text-blue-700 uppercase tracking-wide"
                      >
                        + Add File
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {(board.knowledgeFiles || []).length === 0 ? (
                      <p className="text-[11px] text-gray-400 italic">No knowledge files yet. Add one to give agents context.</p>
                    ) : (
                      (board.knowledgeFiles || []).map(file => (
                        <div key={file._id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 hover:bg-gray-100 group">
                          <button
                            onClick={() => setEditingKnowledgeFile(file)}
                            className="flex items-center gap-2 text-left flex-1 min-w-0"
                          >
                            <span className="text-gray-400">📄</span>
                            <span className="text-xs font-medium text-gray-700 truncate">{file.name}</span>
                          </button>
                          {canEditBoard && (
                            <button
                              onClick={() => handleDeleteKnowledgeFile(file._id)}
                              className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 text-xs ml-2 transition-opacity"
                              title="Delete file"
                            >
                              🗑
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 italic">
                    Agents will use these files as context when mentioned on this board.
                  </p>
                </div>

                {/* Archived Cards Section */}
                <div className="pt-6 border-t">
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-bold text-gray-700">Archived Cards</label>
                    <button 
                      onClick={fetchArchivedCards}
                      className="text-[10px] bg-gray-100 px-2 py-1 rounded hover:bg-gray-200 font-bold"
                    >
                      {loadingArchived ? 'Loading...' : 'Refresh Archive'}
                    </button>
                  </div>
                  
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-200">
                    {archivedCards.length === 0 ? (
                      <p className="text-xs text-gray-400 italic text-center py-4">No archived cards found.</p>
                    ) : (
                      archivedCards.map(card => (
                        <div key={card._id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex justify-between items-center">
                          <div className="min-w-0 flex-1 mr-4">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{card.title}</p>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400">Archived in {card.column}</p>
                          </div>
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleRestoreCard(card._id)}
                              className="text-[10px] text-primary-blue font-bold hover:underline"
                            >
                              Restore
                            </button>
                            <button 
                              onClick={() => handleDeleteCard(card._id)}
                              className="text-[10px] text-red-600 font-bold hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Pin to dashboard */}
                <div className="pt-6 border-t border-gray-100">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest">Pin to Dashboard</h4>
                      <p className="text-[10px] text-gray-400 mt-1 italic">Pinned boards show as a full-width hero at the top of the dashboard.</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!board.isPinned}
                      onChange={(e) => updateBoardSettings({ isPinned: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-primary-blue focus:ring-primary-blue"
                    />
                  </label>
                </div>

                {/* Archive */}
                <div className="pt-6 border-t border-gray-100">
                  <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest mb-4">Archive</h4>
                  <button
                    onClick={() => handleArchiveBoard(!board.isArchived)}
                    className="w-full py-2 bg-gray-50 text-gray-600 text-xs font-bold rounded-lg border border-gray-200 hover:bg-gray-600 hover:text-white transition-all"
                  >
                    {board.isArchived ? 'Unarchive This Board' : 'Archive This Board'}
                  </button>
                  <p className="text-[10px] text-gray-400 mt-2 italic">
                    Archived boards move to the Archived section on the dashboard and drop out of the All Tasks list. Nothing is deleted.
                  </p>
                </div>

                {/* Danger Zone */}
                <div className="pt-6 border-t border-red-100">
                  <h4 className="text-xs font-black text-red-600 uppercase tracking-widest mb-4">Danger Zone</h4>
                  {!showDeleteConfirm ? (
                    <button 
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full py-2 bg-red-50 text-red-600 text-xs font-bold rounded-lg border border-red-200 hover:bg-red-600 hover:text-white transition-all"
                    >
                      Delete This Board
                    </button>
                  ) : (
                    <div className="space-y-3 animate-fade-in">
                      <p className="text-[10px] text-gray-500 font-bold leading-tight">
                        This action is PERMANENT. All cards, attachments, and activity will be erased forever.
                        Type <span className="text-red-600 font-black">"{board.name}"</span> to confirm.
                      </p>
                      <input 
                        type="text" 
                        className="w-full border-red-200 rounded-lg p-2 text-xs focus:ring-red-500 focus:border-red-500"
                        placeholder="Enter board name..."
                        value={deleteBoardName}
                        onChange={(e) => setDeleteConfirmName(e.target.value)}
                      />
                      <div className="flex space-x-2">
                        <button 
                          onClick={handleDeleteBoard}
                          disabled={deleteBoardName !== board.name}
                          className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${deleteBoardName === board.name ? 'bg-red-600 text-white shadow-lg' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                        >
                          Delete Forever
                        </button>
                        <button 
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmName('');
                          }}
                          className="px-4 py-2 text-xs font-bold text-gray-500 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <button 
                    onClick={() => updateBoardSettings({ background: { type: 'color', value: '#f3f4f6' }, defaultView: 'kanban' })}
                    className="text-xs text-gray-500 hover:text-primary-blue transition-colors"
                  >
                    Reset to default
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Card Modal */}
      {showConsolidateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => !consolidating && setShowConsolidateModal(false)}></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                <h3 className="text-lg leading-6 font-black uppercase tracking-tighter text-gray-900 dark:text-white mb-1">
                  Consolidate {selectedCards.size} Cards
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Creates one new card with each selected card's title as a subtask.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Parent Title</label>
                    <input
                      type="text"
                      autoFocus
                      value={consolidateForm.title}
                      onChange={(e) => setConsolidateForm({ ...consolidateForm, title: e.target.value })}
                      placeholder="e.g. Shred Day prep"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Priority</label>
                      <select
                        value={consolidateForm.priority}
                        onChange={(e) => setConsolidateForm({ ...consolidateForm, priority: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {['Low','Medium','High','Urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Due Date</label>
                      <input
                        type="date"
                        value={consolidateForm.dueDate}
                        onChange={(e) => setConsolidateForm({ ...consolidateForm, dueDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Column</label>
                    <select
                      value={consolidateForm.column}
                      onChange={(e) => setConsolidateForm({ ...consolidateForm, column: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {board.columns.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Originals</label>
                    <div className="space-y-1">
                      {[
                        { v: 'archive', label: 'Archive originals', desc: 'Hidden but recoverable' },
                        { v: 'delete', label: 'Delete originals', desc: 'Gone for good' },
                        { v: 'keep', label: 'Keep originals', desc: 'Leave them on the board' }
                      ].map(opt => (
                        <label key={opt.v} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                          <input
                            type="radio"
                            name="originalsAction"
                            checked={consolidateForm.originalsAction === opt.v}
                            onChange={() => setConsolidateForm({ ...consolidateForm, originalsAction: opt.v })}
                          />
                          <span className="text-gray-900 dark:text-white font-medium">{opt.label}</span>
                          <span className="text-xs text-gray-500">{opt.desc}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  disabled={consolidating || !consolidateForm.title.trim()}
                  onClick={submitConsolidate}
                  className="w-full sm:w-auto inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-blue text-base font-bold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {consolidating ? 'Consolidating...' : 'Consolidate'}
                </button>
                <button
                  type="button"
                  disabled={consolidating}
                  onClick={() => setShowConsolidateModal(false)}
                  className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowAddModal(false)}></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleAddCard}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 uppercase tracking-tighter font-black">
                    Add New {newCardData.cardType === 'task' ? 'Card' : newCardData.cardType === 'company' ? 'Company' : newCardData.cardType === 'person' ? 'Person' : 'Social Post'}
                  </h3>
                  <div className="space-y-4">
                    {/* Template Selector */}
                    {templates.length > 0 && newCardData.cardType === 'task' && (
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Use Template</label>
                        <div className="grid grid-cols-2 gap-2">
                          {/* No template option */}
                          <button
                            type="button"
                            onClick={() => handleTemplateSelect(null)}
                            className="p-2.5 text-left border border-dashed border-gray-200 rounded-lg hover:border-gray-400 transition-all group"
                          >
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-tight">No Template</p>
                            <p className="text-[9px] text-gray-300 mt-0.5">Clear & start blank</p>
                          </button>
                          {templates.map(t => {
                            const priorityColors = { Low: 'bg-green-100 text-green-700', Medium: 'bg-yellow-100 text-yellow-700', High: 'bg-orange-100 text-orange-700', Urgent: 'bg-red-100 text-red-700' };
                            const badgeClass = priorityColors[t.defaultPriority] || priorityColors.Medium;
                            const strippedDesc = stripMarkdown(t.descriptionTemplate);
                            const preview = strippedDesc ? strippedDesc.slice(0, 60) + (strippedDesc.length > 60 ? '…' : '') : '';
                            return (
                              <button
                                key={t._id}
                                type="button"
                                onClick={() => handleTemplateSelect(t)}
                                className="p-2.5 text-left border border-gray-200 rounded-lg hover:border-primary-blue hover:shadow-sm transition-all"
                              >
                                <div className="flex items-start justify-between gap-1 mb-1">
                                  <p className="text-[10px] font-black text-gray-800 uppercase tracking-tight leading-tight">{t.name}</p>
                                  <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase flex-shrink-0 ${badgeClass}`}>{t.defaultPriority || 'Medium'}</span>
                                </div>
                                {preview && <p className="text-[9px] text-gray-400 italic leading-snug">{preview}</p>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {labelOptions.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Labels</label>
                        <div className="flex flex-wrap gap-1.5">
                          {labelOptions.map(l => {
                            const selected = (newCardData.labels || []).some(x => x.text === l.text);
                            return (
                              <button
                                key={l.text}
                                type="button"
                                onClick={() => setNewCardData({
                                  ...newCardData,
                                  labels: selected
                                    ? newCardData.labels.filter(x => x.text !== l.text)
                                    : [...(newCardData.labels || []), { text: l.text, color: l.color }]
                                })}
                                className={`px-3 py-1 rounded-full text-[10px] font-bold text-white shadow-sm transition-all ${selected ? 'ring-2 ring-offset-1 ring-primary-blue' : 'opacity-50 hover:opacity-100'}`}
                                style={{ backgroundColor: l.color }}
                              >
                                {l.text}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase mb-1">
                        {newCardData.cardType === 'company' ? 'Company Name' : newCardData.cardType === 'social' ? 'Post Title / Internal Name' : 'Title / Name'}
                      </label>
                      <input
                        type="text"
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-blue focus:border-primary-blue sm:text-sm font-bold"
                        value={newCardData.title}
                        onChange={(e) => setNewCardData({ ...newCardData, title: e.target.value })}
                      />
                    </div>

                    {/* Social Media Fields (Conditional) */}
                    {newCardData.cardType === 'social' && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-3 animate-fade-in">
                        <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Social Media Details</p>
                        
                        <div>
                          <label className="block text-[8px] font-black text-blue-400 uppercase mb-2">Target Platforms</label>
                          <div className="flex space-x-2">
                            {['Facebook', 'LinkedIn', 'X'].map(p => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => {
                                  const platforms = newCardData.socialMedia.platforms.includes(p)
                                    ? newCardData.socialMedia.platforms.filter(plat => plat !== p)
                                    : [...newCardData.socialMedia.platforms, p];
                                  setNewCardData({ ...newCardData, socialMedia: { ...newCardData.socialMedia, platforms }});
                                }}
                                className={`px-2 py-1 text-[10px] font-bold rounded border transition-all ${newCardData.socialMedia.platforms.includes(p) ? 'bg-primary-blue text-white border-primary-blue' : 'bg-white text-gray-400 border-gray-200'}`}
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                        </div>

                        {newCardData.socialMedia.platforms.length > 0 && (
                          <div className="space-y-3">
                            {newCardData.socialMedia.platforms.map(p => (
                              <div key={p} className="animate-fade-in">
                                <label className="block text-[8px] font-black text-blue-400 uppercase mb-1">{p} Copy</label>
                                <textarea 
                                  className="w-full text-xs border-blue-100 rounded p-1.5 focus:ring-primary-blue bg-white"
                                  rows="2"
                                  placeholder={`What are we posting on ${p}?`}
                                  value={newCardData.socialMedia.platformCopy[p.toLowerCase()]}
                                  onChange={(e) => setNewCardData({ 
                                    ...newCardData, 
                                    socialMedia: { 
                                      ...newCardData.socialMedia, 
                                      platformCopy: { ...newCardData.socialMedia.platformCopy, [p.toLowerCase()]: e.target.value } 
                                    }
                                  })}
                                ></textarea>
                              </div>
                            ))}
                          </div>
                        )}

                        <div>
                          <label className="block text-[8px] font-black text-blue-400 uppercase mb-1">General Notes / Default Copy</label>
                          <textarea 
                            className="w-full text-xs border-blue-100 rounded p-1.5 focus:ring-primary-blue"
                            rows="2"
                            placeholder="Optional general notes or shared copy..."
                            value={newCardData.socialMedia.postCopy}
                            onChange={(e) => setNewCardData({ ...newCardData, socialMedia: { ...newCardData.socialMedia, postCopy: e.target.value }})}
                          ></textarea>
                        </div>

                        <div>
                          <label className="block text-[8px] font-black text-blue-400 uppercase mb-1">CTA Link</label>
                          <input 
                            type="text" 
                            placeholder="https://..."
                            className="w-full text-xs border-blue-100 rounded p-1.5 focus:ring-primary-blue"
                            value={newCardData.socialMedia.ctaLink}
                            onChange={(e) => setNewCardData({ ...newCardData, socialMedia: { ...newCardData.socialMedia, ctaLink: e.target.value }})}
                          />
                        </div>
                      </div>
                    )}

                    {/* Contact Info Fields (Conditional) */}
                    {(newCardData.cardType === 'person' || newCardData.cardType === 'company') && (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3 animate-fade-in">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Contact Information</p>
                        
                        {newCardData.cardType === 'person' && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[8px] font-black text-gray-400 uppercase">First Name</label>
                              <input 
                                type="text" 
                                className="w-full text-xs border-gray-200 rounded p-1.5"
                                value={newCardData.contactInfo.firstName}
                                onChange={(e) => setNewCardData({ ...newCardData, contactInfo: { ...newCardData.contactInfo, firstName: e.target.value }})}
                              />
                            </div>
                            <div>
                              <label className="block text-[8px] font-black text-gray-400 uppercase">Last Name</label>
                              <input 
                                type="text" 
                                className="w-full text-xs border-gray-200 rounded p-1.5"
                                value={newCardData.contactInfo.lastName}
                                onChange={(e) => setNewCardData({ ...newCardData, contactInfo: { ...newCardData.contactInfo, lastName: e.target.value }})}
                              />
                            </div>
                          </div>
                        )}

                        {(newCardData.cardType === 'person' || newCardData.cardType === 'company') && (
                          <>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[8px] font-black text-gray-400 uppercase">Email</label>
                                <input 
                                  type="email" 
                                  className="w-full text-xs border-gray-200 rounded p-1.5"
                                  value={newCardData.contactInfo.email}
                                  onChange={(e) => setNewCardData({ ...newCardData, contactInfo: { ...newCardData.contactInfo, email: e.target.value }})}
                                />
                              </div>
                              <div>
                                <label className="block text-[8px] font-black text-gray-400 uppercase">Phone</label>
                                <input 
                                  type="text" 
                                  className="w-full text-xs border-gray-200 rounded p-1.5"
                                  value={newCardData.contactInfo.phone}
                                  onChange={(e) => setNewCardData({ ...newCardData, contactInfo: { ...newCardData.contactInfo, phone: e.target.value }})}
                                />
                              </div>
                            </div>
                            
                            {newCardData.cardType === 'person' && (
                              <div>
                                <label className="block text-[8px] font-black text-gray-400 uppercase">Company Name</label>
                                <input 
                                  type="text" 
                                  className="w-full text-xs border-gray-200 rounded p-1.5"
                                  value={newCardData.contactInfo.companyName}
                                  onChange={(e) => setNewCardData({ ...newCardData, contactInfo: { ...newCardData.contactInfo, companyName: e.target.value }})}
                                />
                              </div>
                            )}

                            <div>
                              <label className="block text-[8px] font-black text-gray-400 uppercase">Website</label>
                              <input 
                                type="text" 
                                className="w-full text-xs border-gray-200 rounded p-1.5"
                                value={newCardData.contactInfo.website}
                                onChange={(e) => setNewCardData({ ...newCardData, contactInfo: { ...newCardData.contactInfo, website: e.target.value }})}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <button 
                          type="button"
                          onClick={handleAiEnhance}
                          disabled={aiLoading}
                          className="text-[10px] font-bold bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 transition-colors flex items-center"
                        >
                          {aiLoading ? 'Thinking...' : '✨ AI Enhance'}
                        </button>
                      </div>
                      <textarea
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-blue focus:border-primary-blue sm:text-sm"
                        rows="5"
                        value={newCardData.description}
                        onChange={(e) => setNewCardData({ ...newCardData, description: e.target.value })}
                      ></textarea>
                    </div>

                    {/* AI Questions Result */}
                    {aiQuestions && (
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-100 animate-fade-in space-y-3">
                        <p className="text-[10px] font-bold text-purple-400 uppercase">AI Suggestions:</p>
                        <div className="text-xs text-purple-900 whitespace-pre-wrap leading-relaxed">
                          {aiQuestions}
                        </div>
                        <div className="pt-2 border-t border-purple-200">
                          <label className="block text-[10px] font-bold text-purple-400 uppercase mb-1">Your Answers</label>
                          <textarea 
                            value={userAiResponse}
                            onChange={(e) => setUserAiResponse(e.target.value)}
                            placeholder="Answer the questions here to refine the card..."
                            className="w-full text-xs border-purple-200 rounded-md focus:ring-purple-500 focus:border-purple-500 p-2 bg-white"
                            rows="3"
                          ></textarea>
                          <button 
                            type="button"
                            onClick={handleAiFormulate}
                            disabled={aiLoading}
                            className="mt-2 w-full py-1.5 bg-purple-600 text-white text-[10px] font-bold rounded hover:bg-purple-700 transition-colors"
                          >
                            {aiLoading ? 'Formulating...' : '✨ Finalize Description with AI'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Assign To (Multiple Users) */}
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Assign To</label>
                      <div className="flex items-center space-x-2">
                        {/* Current Avatars */}
                        <div className="flex -space-x-2 overflow-hidden">
                          {newCardData.assignedTo.map(id => {
                            const user = [currentUser, ...getCollaborators()].find(u => u._id === id);
                            if (!user) return null;
                            return (
                              <div key={id} className="inline-block h-8 w-8 rounded-full ring-2 ring-white overflow-hidden bg-gray-200 shadow-sm" title={user.name}>
                                {user.avatar ? (
                                  <img src={getFileUrl(user.avatar)} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center bg-secondary-cyan text-white text-[10px] font-black">{user.name?.charAt(0)}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Add Button */}
                        <button
                          type="button"
                          onClick={() => setShowAssigneeList(!showAssigneeList)}
                          className={`h-8 w-8 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-primary-blue hover:text-primary-blue transition-all ${showAssigneeList ? 'border-primary-blue text-primary-blue bg-primary-blue/5' : ''}`}
                          title="Add/Remove Assignees"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                        </button>
                      </div>

                      {/* Toggleable Dropdown List */}
                      {showAssigneeList && (
                        <div className="mt-3 bg-gray-50 rounded-xl border border-gray-200 p-2 space-y-1 max-h-48 overflow-y-auto scrollbar-thin animate-fade-in shadow-inner">
                          {[currentUser, ...getCollaborators()]
                            .filter(u => u && u._id) // Ensure valid users
                            .sort((a, b) => {
                              const aAssigned = newCardData.assignedTo.includes(a._id);
                              const bAssigned = newCardData.assignedTo.includes(b._id);
                              if (aAssigned && !bAssigned) return -1;
                              if (!aAssigned && bAssigned) return 1;
                              const nameA = a.name || a.email || '';
                              const nameB = b.name || b.email || '';
                              return nameA.localeCompare(nameB);
                            })
                            .map(user => {
                              const isAssigned = newCardData.assignedTo.includes(user._id);
                              return (
                                <button
                                  key={user._id}
                                  type="button"
                                  onClick={() => {
                                    const assignedTo = isAssigned
                                      ? newCardData.assignedTo.filter(id => id !== user._id)
                                      : [...newCardData.assignedTo, user._id];
                                    setNewCardData({ ...newCardData, assignedTo });
                                  }}
                                  className={`w-full flex items-center justify-between p-2 rounded-lg transition-all ${isAssigned ? 'bg-white shadow-sm ring-1 ring-primary-blue/30' : 'hover:bg-white/50'}`}
                                >
                                  <div className="flex items-center space-x-3 min-w-0">
                                    <div className="h-6 w-6 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 shadow-sm border border-white">
                                      {user.avatar ? (
                                        <img src={getFileUrl(user.avatar)} alt="" className="h-full w-full object-cover" />
                                      ) : (
                                        <div className="h-full w-full flex items-center justify-center bg-gray-300 text-white text-[8px] font-black">{user.name?.charAt(0)}</div>
                                      )}
                                    </div>
                                    <span className={`text-xs truncate ${isAssigned ? 'font-bold text-primary-blue' : 'text-gray-600'}`}>
                                      {user._id === currentUser._id ? 'Assign Me' : user.name}
                                    </span>
                                  </div>
                                  {isAssigned && (
                                    <div className="h-4 w-4 bg-primary-blue rounded-full flex items-center justify-center shadow-sm">
                                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7"></path></svg>
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Priority</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-blue focus:border-primary-blue sm:text-sm"
                        value={newCardData.priority}
                        onChange={(e) => setNewCardData({ ...newCardData, priority: e.target.value })}
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                      </select>
                    </div>

                    {/* Save as Template */}
                    {newCardData.cardType === 'task' && (
                      <div>
                        {!showSaveTemplateForm ? (
                          <button
                            type="button"
                            onClick={() => { setShowSaveTemplateForm(true); setSaveTemplateSuccess(false); }}
                            className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-primary-blue transition-colors border border-dashed border-gray-200 hover:border-primary-blue px-3 py-1.5 rounded-lg w-full"
                          >
                            + Save as Template
                          </button>
                        ) : (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg animate-fade-in">
                            {saveTemplateSuccess ? (
                              <p className="text-[11px] font-black text-green-600 uppercase text-center tracking-wide">Template saved!</p>
                            ) : (
                              <>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5">Template Name</label>
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    autoFocus
                                    placeholder="e.g. Bug Report"
                                    className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-blue"
                                    value={saveTemplateName}
                                    onChange={(e) => setSaveTemplateName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveAsTemplate(saveTemplateName); } }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleSaveAsTemplate(saveTemplateName)}
                                    className="bg-primary-blue text-white text-[10px] font-black uppercase px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setShowSaveTemplateForm(false); setSaveTemplateName(''); }}
                                    className="text-gray-400 text-[10px] font-black uppercase px-2 py-1.5 rounded-lg hover:text-gray-600"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-blue text-white font-medium hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Add Card
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-gray-700 font-medium hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Board;